import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, Calendar, Clock, User, Plus, Loader2, Eye, EyeOff, Settings } from "lucide-react";
import { blogAPI } from "../services/api";
import { useToast } from "../hooks/useToast";

const BlogInternalPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const { toast } = useToast();

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadBlogs();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const loadBlogs = async () => {
    if (searchTerm) {
      setSearchLoading(true);
    }

    try {
      const response = await blogAPI.getBlogs({
        search: searchTerm,
        includeInternal: true, // Include both public and internal blogs
        limit: 20
      });

      if (response.success) {
        setBlogs(response.data.blogs || response.data || []);
        setTotalCount(response.data.totalCount || response.data.total || response.data.length || 0);
      } else {
        toast.error("Failed to load blog posts");
        setBlogs([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error loading blogs:", error);
      toast.error("Failed to load blog posts");
      setBlogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-2 text-slate-600">Loading blog posts...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">Internal Blog</h1>
              <p className="text-xl text-slate-600">
                All blog posts - public and internal content
              </p>
            </div>
            <div className="flex gap-3">
              <Button 
                asChild
                variant="outline"
                className="flex items-center gap-2"
              >
                <Link to="/blog">
                  <Eye className="w-4 h-4" />
                  Public Blog
                </Link>
              </Button>
              <Button
                asChild
                className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-2"
              >
                <Link to="/admin">
                  <Settings className="w-4 h-4" />
                  Admin Dashboard
                </Link>
              </Button>
              <Button
                onClick={() => navigate('/blog/create')}
                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Post
              </Button>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search blog posts..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          

        </div>

        {/* Results Summary */}
        <div className="mb-6 flex items-center justify-between">
          <p className="text-slate-600">
            {searchLoading ? (
              <span className="flex items-center">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Searching...
              </span>
            ) : (
              `${totalCount} blog post${totalCount !== 1 ? 's' : ''} found`
            )}
          </p>
          
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-white border border-slate-200 rounded"></div>
              <span>Public</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-slate-100 border border-slate-300 rounded opacity-60"></div>
              <span>Internal</span>
            </div>
          </div>
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogs.map((blog) => (
            <Card 
              key={blog.id} 
              className={`group hover:shadow-lg transition-all duration-200 border-slate-200 hover:border-blue-200 ${
                blog.visibility === 'internal' ? 'opacity-60 bg-slate-50' : 'bg-white'
              }`}
            >
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between mb-3">
                  {blog.tags && blog.tags.length > 0 && (
                    <Badge
                      variant="secondary"
                      className={blog.visibility === 'internal' ? 'bg-slate-200 text-slate-600' : ''}
                    >
                      {blog.tags[0]}
                    </Badge>
                  )}
                  <div className="flex items-center gap-1">
                    {blog.visibility === 'internal' ? (
                      <EyeOff className="w-4 h-4 text-slate-400" title="Internal post" />
                    ) : (
                      <Eye className="w-4 h-4 text-green-500" title="Public post" />
                    )}
                  </div>
                </div>
                
                <h3 className="text-xl font-semibold text-slate-900 group-hover:text-blue-600 transition-colors duration-200 line-clamp-2">
                  <Link to={`/blog/${blog.slug}`}>
                    {blog.title}
                  </Link>
                </h3>
              </CardHeader>
              
              <CardContent>
                <p className="text-slate-600 mb-4 line-clamp-3">
                  {blog.excerpt}
                </p>
                
                <div className="flex items-center justify-between text-sm text-slate-500">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-1" />
                      {blog.author}
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {new Date(blog.publishedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    {blog.readTime} min read
                  </div>
                </div>
                
                {blog.tags && blog.tags.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {blog.tags.slice(0, 3).map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                    {blog.tags.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{blog.tags.length - 3} more
                      </Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {!loading && blogs.length === 0 && (
          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <div className="mb-4">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">No blog posts found</h3>
                <p className="text-slate-600">
                  {searchTerm
                    ? "Try adjusting your search criteria."
                    : "No blog posts have been created yet."
                  }
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {searchTerm && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchTerm("");
                    }}
                  >
                    Clear Search
                  </Button>
                )}
                <Button
                  onClick={() => navigate('/blog/create')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Post
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogInternalPage;
